import { useState } from "react";
import { Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { InfoPopover } from "@/components/cinch/InfoPopover";

import {
  fmtQty,
  fmtRate,
  monthLabel,
  useCalcSettings,
  useCosMetrics,
  useCosPeriod,
  useResetCalcSetting,
  useCreateProductLine,
  useSetCalcSetting,
  useSetCosMetric,
  type CalcSettingRead,
  type MetricSource,
  type CapacityMethod,
} from "../api";

const METHOD_LABEL: Record<string, string> = {
  actual: "Actual ($ ÷ units)",
  target_rate: "Target rate (contracted $/unit)",
  marginal: "Marginal (strip allocated overhead)",
  none: "None (dollars only)",
};

const SOURCE_LABEL: Record<string, string> = {
  invoice: "Uploaded invoices",
  recon: "Integration reconciliation",
  cinch: "Cinch data (Snowflake)",
};

const SOURCE_HELP: Record<string, string> = {
  invoice:
    "The metric quantity comes from the uploaded files: the workbook's Simple Summary, a manual value, or summed line-item quantities.",
  recon:
    "The quantity comes from the Integration Expense Reconciliation's billed-location count for the month (ISI's per-location invoice lines) — entered once, over there. Falls back to the uploaded files when recon has no invoice for the month.",
  cinch:
    "Reserved: the quantity will come from Cinch's billing data in Snowflake once the pricing-metrics → billing-items queries are settled. Until then it behaves like Uploaded invoices.",
};

const METHOD_HELP: Record<string, string> = {
  actual: "Full-capacity $/metric equals the actual $/metric — total mapped cost divided by the metric quantity.",
  target_rate:
    "Full-capacity $/metric is the contracted rate (e.g. Voice $0.07/min, OEM $4.25/location). Capacity = total cost ÷ rate; utilization = units ÷ capacity.",
  marginal:
    "Excl-tax $/metric strips the overhead allocated into the line by split rules (Twilio support + taxes) before dividing; incl-tax is the full actual.",
  none: "A flat or one-off cost with no meaningful per-unit metric (short codes, landing pages).",
};

/**
 * The calculations behind the unit costs: each product line's capacity method
 * and target rates (durable config), and the month's metric denominators
 * (emails sent, text credits, mailer pieces…) with their provenance.
 */
export function CalculationsView({ period }: { period: string }) {
  const settings = useCalcSettings();
  const { data: detail } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const signedOff = detail?.status === "signed_off";
  const metrics = useCosMetrics(period, exists);
  const setMetric = useSetCosMetric(period);
  const reset = useResetCalcSetting(period);
  const [editing, setEditing] = useState<CalcSettingRead | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const settingColumns: DataTableColumn<CalcSettingRead>[] = [
    {
      key: "title",
      header: "Product line",
      render: (s) => (
        <div>
          <div className="flex items-center gap-1.5 font-medium">
            {s.title}
            {s.is_override && <Badge variant="secondary">customized</Badge>}
          </div>
          <div className="text-xs text-muted-foreground">{s.metric_label}</div>
        </div>
      ),
    },
    {
      key: "method",
      header: "Capacity method",
      render: (s) => (
        <div className="flex items-center gap-1.5">
          <span>{METHOD_LABEL[s.capacity_method] ?? s.capacity_method}</span>
          <InfoPopover title={METHOD_LABEL[s.capacity_method] ?? s.capacity_method}>
            {METHOD_HELP[s.capacity_method] ?? ""}
          </InfoPopover>
        </div>
      ),
    },
    {
      key: "rate_excl",
      header: "Target rate excl tax",
      align: "right",
      numeric: true,
      render: (s) =>
        s.capacity_method === "target_rate" ? (
          <span>
            {fmtRate(s.target_rate_excl_tax)}/{s.unit_label}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "rate_incl",
      header: "Target rate incl tax",
      align: "right",
      numeric: true,
      render: (s) =>
        s.capacity_method === "target_rate" ? (
          <span>
            {fmtRate(s.target_rate_incl_tax)}
            {s.target_rate_incl_tax != null ? `/${s.unit_label}` : ""}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "metric_source",
      header: "Quantity from",
      render: (s) => (
        <div className="flex items-center gap-1.5">
          <span>{SOURCE_LABEL[s.metric_source ?? "invoice"]}</span>
          <InfoPopover title={SOURCE_LABEL[s.metric_source ?? "invoice"] ?? "Uploaded invoices"}>
            {SOURCE_HELP[s.metric_source ?? "invoice"]}
          </InfoPopover>
        </div>
      ),
    },
    {
      key: "notes",
      header: "Notes",
      render: (s) =>
        s.notes ? (
          <span className="block max-w-[320px] truncate text-xs text-muted-foreground" title={s.notes}>
            {s.notes}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        columns={settingColumns}
        rows={settings.data ?? []}
        rowKey={(s) => s.product_line_key}
        loading={settings.isLoading}
        emptyMessage="No product lines."
        onRowClick={(s) => setEditing(s)}
        selectedKey={editing?.product_line_key ?? null}
        toolbar={
          <span className="text-xs text-muted-foreground">
            Click a line to change how its unit cost is calculated
          </span>
        }
      />

      <AddProductLineCard period={period} />

      <Card className="p-4">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            {monthLabel(period)} metric quantities (the denominators)
          </h2>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          $/metric = the line&apos;s mapped cost ÷ this quantity. Parsed off the workbook&apos;s
          Simple Summary where possible; type a value to override it (manual wins), clear
          it to fall back. Mailer pieces derive from the batch file when nothing is set.
        </p>
        {!exists ? (
          <p className="text-sm text-muted-foreground">
            This month hasn&apos;t been opened yet — upload its cost files first.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(metrics.data ?? []).map((m) => {
              const setting = (settings.data ?? []).find(
                (s) => s.product_line_key === m.product_line_key,
              );
              const draft = drafts[m.product_line_key];
              const shown = draft ?? (m.metric_units != null ? String(m.metric_units) : "");
              return (
                <div key={m.product_line_key} className="flex flex-col gap-1">
                  <Label className="flex items-center justify-between text-xs">
                    <span>
                      {setting?.title ?? m.product_line_key}
                      <span className="ml-1 text-muted-foreground">
                        ({setting?.metric_label ?? "units"})
                      </span>
                    </span>
                    {m.source && <Badge variant="secondary">{m.source}</Badge>}
                  </Label>
                  <div className="flex gap-1.5">
                    <Input
                      inputMode="decimal"
                      disabled={signedOff}
                      value={shown}
                      placeholder="—"
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [m.product_line_key]: e.target.value }))
                      }
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      disabled={signedOff || draft === undefined || setMetric.isPending}
                      onClick={() => {
                        const raw = (draft ?? "").replace(/[$,\s]/g, "");
                        const value = raw === "" ? null : Number(raw);
                        if (value !== null && Number.isNaN(value)) {
                          toast.error("Not a number");
                          return;
                        }
                        setMetric.mutate(
                          { product_line_key: m.product_line_key, metric_units: value },
                          {
                            onSuccess: () => {
                              setDrafts((d) => {
                                const next = { ...d };
                                delete next[m.product_line_key];
                                return next;
                              });
                              toast.success(
                                value === null
                                  ? "Cleared — falls back to workbook/derived"
                                  : `Set to ${fmtQty(value)}`,
                              );
                            },
                            onError: (e) =>
                              toast.error("Couldn't save", {
                                description: (e as Error).message,
                              }),
                          },
                        );
                      }}
                    >
                      {setMetric.isPending ? "…" : "Set"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <CalcSettingDrawer
        period={period}
        setting={editing}
        onClose={() => setEditing(null)}
        onReset={(lineKey) =>
          reset.mutate(lineKey, {
            onSuccess: () => {
              toast.success("Reverted to the registry default");
              setEditing(null);
            },
            onError: (e) => toast.error("Couldn't reset", { description: (e as Error).message }),
          })
        }
      />
    </div>
  );
}

function CalcSettingDrawer({
  period,
  setting,
  onClose,
  onReset,
}: {
  period: string;
  setting: CalcSettingRead | null;
  onClose: () => void;
  onReset: (lineKey: string) => void;
}) {
  const save = useSetCalcSetting(period);
  const [expanded, setExpanded] = useState(false);
  const [method, setMethod] = useState<CapacityMethod>("actual");
  const [rateExcl, setRateExcl] = useState("");
  const [rateIncl, setRateIncl] = useState("");
  const [source, setSource] = useState<MetricSource>("invoice");
  const [notes, setNotes] = useState("");
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (setting != null && seededFor !== setting.product_line_key) {
    setSeededFor(setting.product_line_key);
    setMethod(setting.capacity_method);
    setRateExcl(setting.target_rate_excl_tax != null ? String(setting.target_rate_excl_tax) : "");
    setRateIncl(setting.target_rate_incl_tax != null ? String(setting.target_rate_incl_tax) : "");
    setSource(setting.metric_source ?? "invoice");
    setNotes(setting.notes ?? "");
  }

  const submit = () => {
    if (setting == null) return;
    const excl = rateExcl.trim() === "" ? null : Number(rateExcl);
    const incl = rateIncl.trim() === "" ? null : Number(rateIncl);
    if ((excl !== null && Number.isNaN(excl)) || (incl !== null && Number.isNaN(incl))) {
      toast.error("Rates must be numbers");
      return;
    }
    save.mutate(
      {
        lineKey: setting.product_line_key,
        payload: {
          capacity_method: method,
          target_rate_excl_tax: excl,
          target_rate_incl_tax: incl,
          metric_source: source,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${setting.title} calculation updated`);
          onClose();
        },
        onError: (e) => toast.error("Couldn't save", { description: (e as Error).message }),
      },
    );
  };

  return (
    <EntityDrawer
      open={setting != null}
      onOpenChange={(open) => !open && onClose()}
      expanded={expanded}
      onExpandedChange={setExpanded}
      title={setting?.title ?? ""}
      subtitle="Unit-cost calculation"
    >
      {setting != null && (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <Label>Capacity method</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as CapacityMethod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(METHOD_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{METHOD_HELP[method]}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Metric quantity from</Label>
            <Select value={source} onValueChange={(v) => setSource(v as MetricSource)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{SOURCE_HELP[source]}</p>
          </div>

          {method === "target_rate" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Target rate excl tax ($/{setting.unit_label})</Label>
                <Input
                  inputMode="decimal"
                  value={rateExcl}
                  onChange={(e) => setRateExcl(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Target rate incl tax ($/{setting.unit_label})</Label>
                <Input
                  inputMode="decimal"
                  value={rateIncl}
                  onChange={(e) => setRateIncl(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="flex items-center justify-between gap-2">
            {setting.is_override ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReset(setting.product_line_key)}
                className="text-muted-foreground"
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Revert to default
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={save.isPending} onClick={submit}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Changes recompute the selected month immediately (signed-off months keep their
            frozen snapshot until reopened).
          </p>
        </div>
      )}
    </EntityDrawer>
  );
}


function AddProductLineCard({ period }: { period: string }) {
  const create = useCreateProductLine(period);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [metricLabel, setMetricLabel] = useState("");
  const [unitLabel, setUnitLabel] = useState("");
  const [method, setMethod] = useState<CapacityMethod>("actual");
  const [source, setSource] = useState<MetricSource>("invoice");

  const submit = () => {
    if (!title.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        metric_label: metricLabel.trim() || "—",
        unit_label: unitLabel.trim() || "—",
        capacity_method: method,
        metric_source: source,
      },
      {
        onSuccess: (line) => {
          toast.success(`Product line "${line.title}" added`, {
            description: "It's now a mapping target — route invoice lines to it on the Mappings tab.",
          });
          setTitle("");
          setMetricLabel("");
          setUnitLabel("");
          setMethod("actual");
          setSource("invoice");
          setOpen(false);
        },
        onError: (e) => toast.error("Couldn't add product line", { description: (e as Error).message }),
      },
    );
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Add a product line</h2>
          <p className="text-xs text-muted-foreground">
            A new bucket to map costs into. Decide up front where its metric quantity
            comes from — the uploaded invoices, the integration reconciliation, or
            Cinch data.
          </p>
        </div>
        {!open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            New product line
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="flex flex-col gap-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Reviews Syndication" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Metric label</Label>
            <Input value={metricLabel} onChange={(e) => setMetricLabel(e.target.value)} placeholder="Locations" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Unit</Label>
            <Input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} placeholder="location" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Capacity method</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as CapacityMethod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(METHOD_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Quantity from</Label>
            <Select value={source} onValueChange={(v) => setSource(v as MetricSource)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 lg:col-span-5">
            <Button size="sm" disabled={!title.trim() || create.isPending} onClick={submit}>
              {create.isPending ? "Adding…" : "Add product line"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
